<script setup lang="ts">
import CopyButton from '@/components/common/CopyButton.vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { useClaims } from '@/composables/asicw/claims'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { ClaimStatusTypes, ClaimStatus, type IClaimDashboard, type IClaimFilters } from '@/types/asicw/claims'
import { Icon } from '@iconify/vue'
import { ElLink } from 'element-plus'
import { computed, reactive, ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import ClaimEditDialog from '@/components/asicw/claims/ClaimEditDialog.vue'
import { useRouter } from 'vue-router'
import ClaimStatusTag from '@/components/asicw/common/ClaimStatusTag.vue'
import { useOIDCStore } from '@/stores/oidc.store'
import ClaimCostsDialog from '../claims/ClaimCostsDialog.vue'
import { dateFormatter } from '@/helpers/ep/formatters'
import { formatCurrency } from '@/util/formatters'

const { isASICWOperationalUser, isASICWManagerUser, isASICWViewerUser } = useOIDCStore()

const selectedClaims = ref<{ id: number; number: string }[]>([])

const tooltipVisible = ref(false)
const tooltipPosition = ref<Record<number, DOMRect>>({})
const currentRowId = ref<number>()
const currentRow = ref<IClaimDashboard>()

const filters = reactive<IClaimFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'created_at',
  order_by_order: OrderBy.DESC,
  customer_id: undefined,
  status: undefined,
  fragment: undefined,
})

const searchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Claim #, Policy # or Miner SN',
  style: 'width: 256px',
  onChange: (val: string) => {
    if (val == '') {
      filters.fragment = undefined
    } else {
      filters.fragment = val
    }
  },
}

// Vue Query
const { getClaimsQuery, deleteClaimMutation, deleteBulkClaimsMutation } = useClaims()
const { isFetching, isError, isRefetching, data, error, refetch } = getClaimsQuery(filters)

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return 'Searching for ' + filters.fragment
  if (filters.fragment && !isFetching.value) return 'No results for "' + filters.fragment + '"'
  else return 'No data'
})

// DELETE mutation
const { mutateAsync: handleDelete } = deleteClaimMutation

/**
 * Delete claim handler. Stop bubbling to prevent reaching the row handler.
 * @param claimNumber the claim number
 * @param e the click event
 */
const onClickDelete = (claimNumber: string, e: MouseEvent) => {
  e.stopPropagation()
  DeleteConfirmationModal(
    'Delete Claim',
    'Are you sure you want to delete this claim? This action cannot be undone',
    claimNumber,
    handleDelete
  )
}

const showEditDialog = ref(false)
const showCostsDialog = ref(false)
const selectedClaimNumber = ref<string>()
const selectedClaim = ref<IClaimDashboard>()

/**
 * Edit claim handler. Stop bubbling to prevent reaching the row handler.
 * @param claim the claim
 * @param e the click event
 */
const onClickEdit = (claim: IClaimDashboard, e: MouseEvent) => {
  e.stopPropagation()
  selectedClaimNumber.value = claim.number
  showEditDialog.value = true
}

const onClickCostsEdit = (claim: IClaimDashboard, e: MouseEvent) => {
  e.stopPropagation()
  selectedClaim.value = claim
  showCostsDialog.value = true
}

const handleEditClose = () => {
  selectedClaimNumber.value = undefined
}

const handleCostDialogClose = () => {
  selectedClaim.value = undefined
}

const handleSelectionChange = (claims: IClaimDashboard[]) => {
  selectedClaims.value = claims.map((c: IClaimDashboard) => {
    return {
      id: c.id,
      number: c.number,
    }
  })
}

// Get available statuses
const { getClaimStatusesQuery, updateClaimStatusMutation } = useClaims()
const { data: statusesData } = getClaimStatusesQuery()

// Compute status options based on API response
const statusOptions = computed(() => {
  if (!statusesData.value) return []

  return statusesData.value.claim_statuses.map((status) => {
    if (status === 'Denied') {
      return {
        label: status,
        value: status,
        color: getStatusColor(status),
        children: statusesData.value.reject_reasons.map((reason) => ({
          label: reason,
          value: `${status}:${reason}`,
        })),
      }
    }
    return {
      label: status,
      value: status,
      color: getStatusColor(status),
    }
  })
})

// Status colors mapping
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    [ClaimStatus.UnderReview]: 'var(--status-under-review)',
    [ClaimStatus.ConditionalApproval]: 'var(--status-conditional-approval)',
    [ClaimStatus.PendingCustomerAuthorization]: 'var(--status-pending-customer-auth)',
    [ClaimStatus.Authorized]: 'var(--status-authorized)',
    [ClaimStatus.Approved]: 'var(--status-approved)',
    [ClaimStatus.Denied]: 'var(--status-denied)',
    [ClaimStatus.Completed]: 'var(--status-completed)',
    [ClaimStatus.Invoiced]: 'var(--status-completed)',
  }
  return colorMap[status] || 'var(--status-default)'
}

const handleStatusChange = async (value: string, row: IClaimDashboard) => {
  if (!value || value === row.status) return

  // If it's a denied status with reason (format: "Denied:Reason")
  try {
    if (value.startsWith('Denied:')) {
      const [status, rejectReason] = value.split(':')
      await updateClaimStatusMutation.mutateAsync({
        claimNumber: row.number,
        statusUpdate: {
          status,
          reject_reason: rejectReason,
        },
      })
    } else {
      // For other statuses, just update the status
      await updateClaimStatusMutation.mutateAsync({
        claimNumber: row.number,
        statusUpdate: { status: value },
      })
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e: any) {
    // Handled by the mutation
    refetch()
  } finally {
    tooltipVisible.value = false
  }
}

const handleDeleteDisabled = (status: ClaimStatus) => {
  const deletableStatuses = [ClaimStatus.UnderReview, ClaimStatus.Denied]
  return !deletableStatuses.includes(status)
}

// Add router
const router = useRouter()

/**
 * Handles row click, navigates to claim details for a given row.
 *
 * @param row The clicked row data
 */
const handleRowClick = (row: IClaimDashboard) => {
  router.push({
    name: 'asicw-claims-details',
    params: { claimNumber: row.number },
  })
}

/**
 * Gets the row class name.
 * Applies "cursor: pointer".
 */
const getRowClassName = () => {
  return 'cursor-pointer'
}

// Bulk delete  claims
const { mutateAsync: handleDeleteBulk } = deleteBulkClaimsMutation

const handleDeleteSelectedClaims = () => {
  DeleteConfirmationModal(
    'Delete Claims',
    `Are you sure you want to delete ${selectedClaims.value.length} claims? This action cannot be undone.`,
    selectedClaims.value.map((claim) => claim.id),
    async (claimIds: string[]) => {
      await handleDeleteBulk(claimIds)
    }
  )
}

const handleRowSelectable = (row: IClaimDashboard) => {
  return !handleDeleteDisabled(row.status as ClaimStatus)
}

const handleTagMouseEnter = (e: MouseEvent, row: IClaimDashboard) => {
  const target = e.target as HTMLElement
  const rect = target.getBoundingClientRect()
  tooltipPosition.value[row.id] = DOMRect.fromRect({
    width: rect.width,
    height: rect.height,
    x: rect.x,
    y: rect.y,
  })
  currentRowId.value = row.id
  currentRow.value = row
  tooltipVisible.value = true
}

const handleTooltipClickOutside = (e: MouseEvent) => {
  const cascaderPanel = document.querySelector('.el-cascader-panel')
  if (cascaderPanel && !cascaderPanel.contains(e.target as Node)) {
    tooltipVisible.value = false
    currentRowId.value = undefined
    currentRow.value = undefined
  }
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="() => refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="claims-table__controls">
      <div class="claims-table__filters">
        <TableSearchFilter :search-settings="searchSettings" :disabled="isFetching" />
      </div>
      <el-button v-if="selectedClaims.length" type="danger" plain class="ml-auto" @click="handleDeleteSelectedClaims">
        <Icon icon="material-symbols:delete-outline" height="16" :inline="true" />&nbsp;Delete
      </el-button>
    </div>
    <el-table
      v-loading="isFetching"
      :data="data?.data"
      :empty-text="tableEmptyText"
      stripe
      :row-class-name="getRowClassName"
      @selection-change="handleSelectionChange"
      @row-click="handleRowClick"
    >
      <template #empty>
        <el-empty />
      </template>
      <el-table-column v-if="isASICWOperationalUser" type="selection" width="55" :selectable="handleRowSelectable" />
      <el-table-column prop="number" label="Claim #" :min-width="176">
        <template #default="scope">
          <el-tooltip effect="light" placement="top-start" :content="scope.row.number">
            <span>
              <copy-button :value="scope.row.number">
                {{ scope.row.number.split('-').pop().toUpperCase() }}
              </copy-button>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="date_of_loss" label="Date of Loss" :min-width="110">
        <template #default="scope">
          <el-tooltip
            effect="light"
            placement="top"
            :content="`Created at ${dateFormatter(scope.row, null, scope.row.created_at)}`"
          >
            <span>{{ dateFormatter(scope.row, null, scope.row.date_of_loss) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="miner.sn" label="Miner" :min-width="200">
        <template #default="scope">
          <span>
            <copy-button :value="scope.row.miner.sn">
              {{ scope.row.miner.sn }}
            </copy-button>
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="policy_number" label="Policy #" :min-width="176" />
      <el-table-column prop="failures" label="Failures" :min-width="80" />
      <el-table-column label="Claim and Repair Status" :min-width="200">
        <template #default="scope">
          <div class="status-tags" @click.stop>
            <ClaimStatusTag
              :status="scope.row.status"
              :type="ClaimStatusTypes.ClaimStatus"
              :reject-reason="scope.row.reject_reason"
              :readonly="!isASICWOperationalUser"
              @click="handleTagMouseEnter($event, scope.row as IClaimDashboard)"
            />
            <ClaimStatusTag
              v-if="scope.row.repair_status"
              :status="scope.row.repair_status"
              :type="ClaimStatusTypes.RepairStatus"
              :readonly="true"
            />
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="work_order_number" label="RWO #" :min-width="200">
        <template #default="scope">
          <template v-if="scope.row.work_order_number">
            <copy-button :value="scope.row.work_order_number">
              <ElLink
                @click="
                  () =>
                    router.push({ name: 'rc-work-order-dashboard', params: { number: scope.row.work_order_number } })
                "
              >
                {{ scope.row.work_order_number }}
              </ElLink>
            </copy-button>
          </template>
          <template v-else>-</template>
        </template>
      </el-table-column>
      <el-table-column v-if="isASICWViewerUser || isASICWManagerUser" prop="cost" label="Cost" :min-width="100">
        <template #default="scope">
          <div v-if="scope.row.costs" class="flex gap-2">
            <span>{{ formatCurrency(scope.row.costs.total) }}</span>
            <el-tooltip v-if="scope.row.costs.details.length" effect="light" placement="top">
              <template #content>
                <p v-for="detail in scope.row.costs.details" :key="detail.id">
                  {{ detail.charge }} ({{ detail.payable_to }}): {{ formatCurrency(detail.cost) }}
                </p>
              </template>
              <el-button link :icon="QuestionFilled" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="isASICWOperationalUser" :min-width="120" align="right" fixed="right">
        <template #default="scope">
          <el-button
            v-if="isASICWManagerUser"
            link
            title="Costs"
            @click="onClickCostsEdit(scope.row as IClaimDashboard, $event)"
          >
            <Icon icon="material-symbols:attach-money" :height="16" />
          </el-button>
          <el-button
            :disabled="scope.row.status === ClaimStatus.Invoiced"
            link
            title="Edit"
            @click="onClickEdit(scope.row as IClaimDashboard, $event)"
          >
            <Icon icon="material-symbols:edit-outline" :height="16" />
          </el-button>
          <el-button
            :disabled="handleDeleteDisabled(scope.row.status)"
            link
            title="Delete"
            @click="onClickDelete(scope.row.number, $event)"
          >
            <Icon icon="material-symbols:delete-outline" :height="16" />
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="claims-table__pagination"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isFetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
    />
    <ClaimEditDialog v-model="showEditDialog" :claim-number="selectedClaimNumber" @close="handleEditClose" />
    <ClaimCostsDialog v-model="showCostsDialog" :claim="selectedClaim" @close="handleCostDialogClose" />
    <el-tooltip
      v-model:visible="tooltipVisible"
      placement="bottom"
      trigger="click"
      virtual-triggering
      :virtual-ref="{
        getBoundingClientRect: () => tooltipPosition[currentRowId!],
      }"
      :show-after="100"
      :hide-after="250"
      effect="light"
      :enterable="true"
      @click-outside="handleTooltipClickOutside"
    >
      <template #content>
        <el-cascader-panel
          v-if="currentRow"
          v-model="currentRow.status"
          :options="statusOptions"
          :props="{
            expandTrigger: 'hover',
          }"
          :border="false"
          @change="(value: any) => handleStatusChange(value.pop() as string, currentRow as IClaimDashboard)"
        >
          <template #default="{ data: casData }">
            <div class="flex items-center flex-row gap-2 target-syntetic-click">
              <span
                v-if="currentRow.status === 'Denied' && casData.label === currentRow?.reject_reason"
                :style="{ color: getStatusColor('Denied') }"
                >{{ casData.label }}</span
              >
              <span v-else :style="{ color: casData.color }">{{ casData.label }}</span>
              <Icon
                v-if="casData.label === currentRow.status || casData.label === currentRow?.reject_reason"
                icon="material-symbols:check"
                :height="16"
                :color="getStatusColor(currentRow.status)"
              />
            </div>
          </template>
        </el-cascader-panel>
      </template>
    </el-tooltip>
  </template>
</template>

<style scoped lang="scss">
.claims-table {
  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }

  &__pagination {
    align-self: flex-end;
  }

  &__controls {
    display: inline-flex;
    justify-content: space-between;
  }
}

/* Ellipsis from the left side */
.policy-number-ellipsis {
  display: inline-block;
  max-width: 10ch;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  direction: rtl;
  text-align: left;
}

.status-tags {
  display: flex;
  flex-flow: column wrap;
  align-items: flex-start;
  gap: 8px;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  :deep(.el-tag__content) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

.el-popper.is-dark {
  background-color: var(--el-bg-color-overlay);
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

:deep(.el-cascader-menu__wrap.el-scrollbar__wrap) {
  height: fit-content;
}

:deep(.el-cascader-node__prefix) {
  display: none;
}

:deep(.el-cascader-node.is-active),
:deep(.el-cascader-node.in-active-path) {
  font-weight: initial !important;
}

:deep(.el-button--danger) {
  --el-color-danger: #f5423e;
}
</style>
